import React, { useState } from "react";
import {
  formatDisplayValue,
  formatFieldLabel,
  hasDisplayValue,
  isPlainValueObject,
  isRenderablePrimitive,
  isUrl,
} from "../../utils/studentDetails";

function UrlValue({ value }) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageFailed) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="student-field-link"
      >
        {value}
      </a>
    );
  }

  return (
    <div className="space-y-3">
      <img
        src={value}
        alt="Student asset"
        className="student-field-media"
        loading="lazy"
        onError={() => {
          setImageFailed(true);
        }}
      />
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="student-field-link"
      >
        Open image
      </a>
    </div>
  );
}

function PrimitiveValue({ value }) {
  const displayValue = formatDisplayValue(value);

  if (isUrl(displayValue)) {
    return <UrlValue value={displayValue} />;
  }

  if (typeof value === "boolean") {
    return <span className="student-field-badge">{displayValue}</span>;
  }

  return <span className="student-field-text">{displayValue}</span>;
}

export default function FieldRenderer({ label, value, depth = 0, path = "" }) {
  if (!hasDisplayValue(value)) {
    return null;
  }

  const displayLabel = formatFieldLabel(label);
  const containerClassName =
    depth > 0
      ? "student-field-card student-field-card-nested"
      : "student-field-card";

  if (isRenderablePrimitive(value)) {
    return (
      <article className={containerClassName}>
        <p className="student-field-label">{displayLabel}</p>
        <div className="mt-2">
          <PrimitiveValue value={value} />
        </div>
      </article>
    );
  }

  if (Array.isArray(value)) {
    const items = value.filter((item) => hasDisplayValue(item));

    return (
      <article className={containerClassName}>
        <p className="student-field-label">{displayLabel}</p>
        {items.length === 0 ? (
          <p className="student-field-empty">Not Available</p>
        ) : items.every((item) => isRenderablePrimitive(item)) ? (
          <ul className="student-field-list">
            {items.map((item, index) => (
              <li key={`${path}-${index}`} className="student-field-list-item">
                <PrimitiveValue value={item} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-3 grid gap-3">
            {items.map((item, index) =>
              isRenderablePrimitive(item) ? (
                <div key={`${path}-${index}`} className="student-inline-chip">
                  <PrimitiveValue value={item} />
                </div>
              ) : (
                <FieldRenderer
                  key={`${path}-${index}`}
                  label={`${displayLabel} ${index + 1}`}
                  value={item}
                  depth={depth + 1}
                  path={`${path}.${index}`}
                />
              ),
            )}
          </div>
        )}
      </article>
    );
  }

  if (isPlainValueObject(value)) {
    const entries = Object.entries(value).filter(([, nestedValue]) =>
      hasDisplayValue(nestedValue),
    );

    return (
      <article className={containerClassName}>
        <p className="student-field-label">{displayLabel}</p>
        {entries.length === 0 ? (
          <p className="student-field-empty">Not Available</p>
        ) : (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {entries.map(([childKey, childValue]) => (
              <FieldRenderer
                key={`${path}.${childKey}`}
                label={childKey}
                value={childValue}
                depth={depth + 1}
                path={`${path}.${childKey}`}
              />
            ))}
          </div>
        )}
      </article>
    );
  }

  return null;
}
